import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import hexagonPattern from '@/icons/hexagon-geometric.webp';
import xLogoWhite from '@/icons/x-team.svg';

import { FaqItem } from '@/components/FaqItem';
import { Header } from '@/components/Header';
import { PageContainer } from '@/components/PageContainer';
import { StepItem } from '@/components/StepItem';

import { HOW_IT_WORKS_STEPS, FAQ_ITEMS } from '@/constants/faq';

import { useAuth } from '@/hooks/useAuth';

function Faq() {
  const { user, signOut } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onSignOut={signOut} />
      <PageContainer>
        <div className="space-y-8">
          {/* Navy Hero Summary Card */}
          <div className="relative overflow-hidden rounded-xl bg-navy-950 px-6 py-7 shadow-lg">
            <img
              src={hexagonPattern}
              alt=""
              className="pointer-events-none absolute inset-0 size-full object-cover opacity-[0.15]"
            />
            <img
              src={xLogoWhite}
              alt="X-Team"
              className="absolute top-6 right-5 size-10 opacity-80"
            />

            <p className="relative mb-4 text-xs font-semibold tracking-wider text-gray-400 uppercase">
              Unleash at a glance
            </p>
            <div className="relative mb-5 flex items-baseline justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-green-400">$625</span>
                <span className="text-sm text-gray-300">every 3 months</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-200">$500</span>
                <span className="text-sm text-gray-300">monthly limit*</span>
              </div>
            </div>

            <div className="relative border-t border-white/10 pt-4">
              <p className="text-right text-xs text-gray-500">
                *Learning and X-Team Events don&apos;t count toward the monthly limit.
              </p>
            </div>
          </div>

          {/* About Section */}
          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">About Unleash</h2>
            <p className="text-sm leading-relaxed text-gray-600">
              Unleash is our benefit designed to help you invest in what energises you, fuels your
              passions, and helps you grow — whether that&apos;s upgrading your workspace, booking a
              trip, hitting the gym, or learning something new.
            </p>
          </section>

          {/* How It Works */}
          <section>
            <h2 className="mb-4 text-xl font-bold text-gray-900">How It Works</h2>
            <div className="space-y-0">
              {HOW_IT_WORKS_STEPS.map((step, index) => (
                <StepItem
                  key={index}
                  number={index + 1}
                  title={step.title}
                  description={step.description}
                  className={`mb-0 py-4 ${index < HOW_IT_WORKS_STEPS.length - 1 ? 'border-b border-gray-100' : ''}`}
                  contentClassName="max-w-lg"
                />
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
              <Link
                to="/categories"
                className="flex items-center gap-1 text-sm font-medium text-red-500 hover:text-red-600"
              >
                Unleash Categories
                <ChevronRight className="size-4" />
              </Link>
            </div>
            <div className="rounded-xl border border-gray-100 bg-white px-6">
              {FAQ_ITEMS.map((faq, index) => (
                <FaqItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </section>
        </div>
      </PageContainer>
    </div>
  );
}

export { Faq };
